import React from "react";
import { Box } from "@mui/material";
import { MULISH } from "../../WireGuardTunnels/VpnDebugger/vpnDebugConstants";
import {
  Card,
  CardHeader,
  GapNotice,
  Hint,
  Mono,
  Pill,
  TS_COLORS,
  TsButton,
} from "../TroubleshootingUi";
import { GapResult, SiteConnectionDetail } from "../troubleshootingInterfaces";
import {
  fetchSavedLoginStatus,
  startSavedLoginTest,
} from "../TroubleshootingUtils/troubleshootingApi";
import {
  readSavedLoginStatus,
  refreshStartedAt,
  savedLoginSystemLabel,
  SavedLoginOutcome,
} from "../TroubleshootingUtils/savedLoginTest";
import { siteFamily } from "../TroubleshootingUtils/siteFamily";
import { useCooldown, waitLabel } from "../TroubleshootingUtils/useCooldown";
import { useTroubleshootingStore } from "../troubleshootingStore/troubleshootingStore";
import { formatRelative } from "../TroubleshootingUtils/formatters";
import { SAMPLE_SAVED_LOGIN_STATUS } from "../TroubleshootingUtils/troubleshootingSampleData";

/**
 * admin allows a new camera-list refresh a minute after the last one
 * finishes (CustomerStatus.get_refresh_allowed); match it so the button
 * doesn't offer a run the server will turn down.
 */
export const SAVED_LOGIN_COOLDOWN_SECONDS = 60;
export const SAVED_LOGIN_POLL_MS = 3000;
/** Give up waiting for the job to start / finish after these. */
export const SAVED_LOGIN_START_WAIT_MS = 30_000;
export const SAVED_LOGIN_FINISH_WAIT_MS = 180_000;

type RunState =
  | { kind: "idle" }
  | { kind: "starting" }
  | { kind: "polling" }
  | { kind: "refused"; message: string }
  | { kind: "not_started" }
  | { kind: "timed_out" }
  | {
      kind: "gap";
      result: Extract<GapResult<unknown>, { status: "error" | "unavailable" }>;
    }
  | { kind: "done"; outcome: Exclude<SavedLoginOutcome, { state: "running" }> };

/**
 * Credential test for VMS and vendor-cloud sites: runs connector-tools'
 * camera-list job, which logs in with the site's saved login exactly as the
 * connector does (Milestone management server, DW direct or Nx cloud,
 * OpenEye direct or OWS, …), and reads the outcome from the site status.
 */
export const SavedLoginTest = ({ site }: { site: SiteConnectionDetail }) => {
  const [run, setRun] = React.useState<RunState>({ kind: "idle" });
  const cooldown = useCooldown();
  const mounted = React.useRef(true);
  const timer = React.useRef<number | null>(null);
  React.useEffect(
    () => () => {
      mounted.current = false;
      if (timer.current != null) window.clearTimeout(timer.current);
    },
    []
  );
  // A different site starts fresh.
  React.useEffect(() => {
    if (timer.current != null) window.clearTimeout(timer.current);
    setRun({ kind: "idle" });
  }, [site.id]);

  const family = savedLoginSystemLabel(site.integration_type_name);
  // RTSP-family sites show this next to the camera ONVIF/RTSP test, so it
  // gets its own title and says what it does differently.
  const rtspFamily = siteFamily(site.integration_type_name) === "rtsp-pull";
  // The Diagnosis all-clear counts a fresh successful login as evidence.
  const setSavedLoginStatus = useTroubleshootingStore(
    (state) => state.setSavedLoginStatus
  );
  const sampleData = useTroubleshootingStore((state) => state.sampleData);
  const sampleScenario = useTroubleshootingStore(
    (state) => state.sampleScenario
  );
  const busy = run.kind === "starting" || run.kind === "polling";

  const start = async () => {
    setRun({ kind: "starting" });
    cooldown.start(SAVED_LOGIN_COOLDOWN_SECONDS);
    // A named sample scenario answers without asking the server (ENG-2039).
    const sample = sampleData
      ? SAMPLE_SAVED_LOGIN_STATUS(sampleScenario)
      : null;
    const sampleOutcome = sample ? readSavedLoginStatus(sample) : null;
    if (sample && sampleOutcome && sampleOutcome.state !== "running") {
      setSavedLoginStatus({ siteId: site.id, status: sample });
      setRun({ kind: "done", outcome: sampleOutcome });
      return;
    }
    // The run is ours once last_refresh_started moves past what it was
    // before we asked (no reliance on the browser's clock).
    let before: number | null = null;
    try {
      before = refreshStartedAt(await fetchSavedLoginStatus(site.id));
    } catch {
      before = null;
    }
    const started = await startSavedLoginTest(site.id);
    if (!mounted.current) return;
    if (started.status !== "ok") {
      setRun({ kind: "gap", result: started });
      if (started.status === "error" && started.retryAfterSeconds)
        cooldown.start(started.retryAfterSeconds);
      return;
    }
    if (started.data.refusedMessage) {
      setRun({ kind: "refused", message: started.data.refusedMessage });
      return;
    }
    setRun({ kind: "polling" });
    const t0 = Date.now();
    const poll = async () => {
      if (!mounted.current) return;
      let status = null;
      try {
        status = await fetchSavedLoginStatus(site.id);
      } catch {
        status = null;
      }
      if (!mounted.current) return;
      const startedAt = refreshStartedAt(status);
      const ours = startedAt != null && (before == null || startedAt > before);
      const elapsed = Date.now() - t0;
      if (ours) {
        const outcome = readSavedLoginStatus(status);
        if (outcome.state !== "running") {
          setSavedLoginStatus({ siteId: site.id, status });
          setRun({ kind: "done", outcome });
          return;
        }
      } else if (elapsed > SAVED_LOGIN_START_WAIT_MS) {
        setRun({ kind: "not_started" });
        return;
      }
      if (elapsed > SAVED_LOGIN_FINISH_WAIT_MS) {
        setRun({ kind: "timed_out" });
        return;
      }
      timer.current = window.setTimeout(poll, SAVED_LOGIN_POLL_MS);
    };
    timer.current = window.setTimeout(poll, SAVED_LOGIN_POLL_MS);
  };

  return (
    <Card testId="saved-login-test">
      <CardHeader
        title={rtspFamily ? "Saved login test" : "Credential test"}
        hint={
          rtspFamily
            ? `Logs in with ${site.name}'s saved login the way auto-add does and lists the cameras. Change the login on the site page. It also refreshes the camera list auto-add shows, and changes nothing else.`
            : `Logs in to ${site.name}'s ${family} with its saved login, the same way the connector does, and lists its cameras. It uses the saved login only; change it on the site page. It also refreshes the camera list auto-add shows, and changes nothing else.`
        }
        right={
          <TsButton
            data-testid="saved-login-run"
            onClick={start}
            disabled={busy || cooldown.remaining > 0}
          >
            {busy
              ? "Testing…"
              : cooldown.remaining > 0
              ? waitLabel(cooldown.remaining)
              : "Test saved login"}
          </TsButton>
        }
      />
      <Box sx={{ px: 2.5, pb: 2.5 }} data-testid="saved-login-result">
        {busy && (
          <Hint>
            Logging in… this usually takes under a minute, longer for a large
            system.
          </Hint>
        )}
        {run.kind === "refused" && (
          <Outcome tone="warn" pill="Didn't run" text={run.message} />
        )}
        {run.kind === "not_started" && (
          <Outcome
            tone="warn"
            pill="Didn't run"
            text="The test didn't start. A camera-list refresh may already be running for this site; try again in a minute."
          />
        )}
        {run.kind === "timed_out" && (
          <Outcome
            tone="warn"
            pill="Still running"
            text="No answer after 3 minutes. The result will show on the site's auto-add page when the job finishes."
          />
        )}
        {run.kind === "gap" && (
          <GapNotice
            result={run.result}
            what="The credential test"
            kind="check"
          />
        )}
        {run.kind === "done" && run.outcome.state === "ok" && (
          <Outcome
            tone="ok"
            pill="Logged in"
            text={`Logged in with the saved login and listed the cameras (${formatRelative(
              new Date(run.outcome.at * 1000).toISOString()
            )}).`}
          />
        )}
        {run.kind === "done" && run.outcome.state === "wrong_login" && (
          <Outcome
            tone="bad"
            pill="Login refused"
            text={`The ${family} refused the saved username or password. Update the login on the site page, then test again.`}
            detail={run.outcome.detail}
          />
        )}
        {run.kind === "done" && run.outcome.state === "failed" && (
          <Outcome
            tone="bad"
            pill="Didn't log in"
            text={run.outcome.message}
            detail={run.outcome.detail}
          />
        )}
      </Box>
    </Card>
  );
};

const Outcome = ({
  tone,
  pill,
  text,
  detail,
}: {
  tone: "ok" | "bad" | "warn";
  pill: string;
  text: string;
  detail?: string;
}) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        flexWrap: "wrap",
      }}
    >
      <Pill tone={tone}>{pill}</Pill>
      <Box
        component="span"
        sx={{ ...MULISH, fontSize: 14, color: TS_COLORS.navy }}
      >
        {text}
      </Box>
    </Box>
    {detail ? (
      <Box
        sx={{
          maxHeight: 160,
          overflow: "auto",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        <Mono>{detail}</Mono>
      </Box>
    ) : null}
  </Box>
);
